'use client';

import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/lib/stores/app-store';
import { API } from '@/lib/utils/api';
import { MapPanel } from '@/components/map/MapPanel';
import { BoardCell } from '@/components/board/BoardCell';
import { STTInput } from '@/components/audio/STTInput';
import { TTSPlayer } from '@/components/audio/TTSPlayer';
import {
  ArrowLeft,
  Map,
  X,
  BookOpen,
  Dumbbell,
  Stethoscope,
  Zap,
  Save,
  Check,
  Volume2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LearningMode, Message, BoardCell as BoardCellType } from '@/lib/types';

export function LearnScreen() {
  const {
    language,
    mode,
    setMode,
    subjectMap,
    selectedNodeId,
    boardCells,
    messages,
    isMapPanelOpen,
    toggleMapPanel,
    addMessage,
    addBoardCell,
    setScreen,
    setLoading,
    isLoading,
    currentSessionId,
    saveCurrentSession,
    updateCurrentSession,
    autoPlayTTS,
    setAutoPlayTTS,
    hasPlayedFirstTTS,
    setHasPlayedFirstTTS,
  } = useAppStore();

  const [boardInput, setBoardInput] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [showSaved, setShowSaved] = useState(false);
  const boardEndRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const isHebrew = language === 'he';
  const selectedNode = subjectMap?.nodes[selectedNodeId || ''];
  const topicTitle = selectedNode
    ? isHebrew
      ? selectedNode.titleHe || selectedNode.title
      : selectedNode.title
    : '';

  const text = {
    en: {
      back: 'Back to Map',
      boardPlaceholder: 'Write on board or ask AI to draw something...',
      chatPlaceholder: 'Talk to your teacher...',
      send: 'Send',
      teachMe: 'Teach Me',
      practice: 'Practice',
      diagnose: 'Diagnose',
      drill: 'Quick Drill',
      save: 'Save',
      saved: 'Saved!',
      autoPlay: 'Auto-play',
    },
    he: {
      back: 'חזור למפה',
      boardPlaceholder: 'כתוב על הלוח או בקש מ-AI לצייר משהו...',
      chatPlaceholder: 'דבר עם המורה שלך...',
      send: 'שלח',
      teachMe: 'למד אותי',
      practice: 'תרגול',
      diagnose: 'אבחון',
      drill: 'תרגיל מהיר',
      save: 'שמור',
      saved: 'נשמר!',
      autoPlay: 'נגן אוטומטי',
    },
  };

  const t = text[language];

  const modes: { value: LearningMode; label: string; icon: React.ReactNode }[] = [
    { value: 'teach', label: t.teachMe, icon: <BookOpen className="w-4 h-4" /> },
    { value: 'practice', label: t.practice, icon: <Dumbbell className="w-4 h-4" /> },
    { value: 'diagnose', label: t.diagnose, icon: <Stethoscope className="w-4 h-4" /> },
    { value: 'drill', label: t.drill, icon: <Zap className="w-4 h-4" /> },
  ];

  // Auto-scroll board and chat
  useEffect(() => {
    boardEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [boardCells]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial greeting when entering learn screen
  useEffect(() => {
    if (messages.length === 0 && selectedNode) {
      sendToTeacher('', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save when messages change (debounced)
  useEffect(() => {
    if (messages.length === 0) return;

    const timer = setTimeout(() => {
      if (currentSessionId) {
        updateCurrentSession();
      } else {
        saveCurrentSession();
      }
    }, 2000); // Save 2 seconds after last message

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, boardCells.length]);

  const handleSave = () => {
    if (currentSessionId) {
      updateCurrentSession();
    } else {
      saveCurrentSession();
    }
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const sendToTeacher = async (userMessage: string, isInitial = false) => {
    if (!isInitial && !userMessage.trim()) return;

    // Add user message to chat (unless initial)
    if (!isInitial) {
      const newMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: userMessage,
        timestamp: Date.now(),
      };
      addMessage(newMessage);
    }

    setLoading(true);
    try {
      const response = await fetch(API.teacher, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: isInitial ? [] : [...messages, { role: 'user', content: userMessage }],
          mode,
          currentTopicId: selectedNodeId,
          boardState: boardCells,
          language,
        }),
      });

      if (!response.ok) throw new Error('Failed to get teacher response');

      const data = await response.json();

      // Add teacher message
      const teacherMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        timestamp: Date.now(),
        boardCommands: data.boardCommands,
      };
      addMessage(teacherMessage);

      // Process board commands
      if (data.boardCommands) {
        for (const cmd of data.boardCommands) {
          if (cmd.action === 'add' && cmd.cell) {
            addBoardCell({
              id: crypto.randomUUID(),
              type: cmd.cell.type || 'text',
              content: cmd.cell.content || '',
              metadata: cmd.cell.metadata || {},
              author: 'teacher',
              timestamp: Date.now(),
            });
          }
        }
      }
    } catch (error) {
      console.error('Error talking to teacher:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardSubmit = async () => {
    if (!boardInput.trim()) return;

    // Add student's input to board
    const newCell: BoardCellType = {
      id: crypto.randomUUID(),
      type: 'text',
      content: boardInput,
      metadata: { description: 'Student input' },
      author: 'student',
      timestamp: Date.now(),
    };
    addBoardCell(newCell);

    // Also notify teacher about what student wrote
    await sendToTeacher(`[Student wrote on board: "${boardInput}"]`);
    setBoardInput('');
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;
    await sendToTeacher(chatInput);
    setChatInput('');
  };

  return (
    <div className="h-screen flex flex-col" dir={isHebrew ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setScreen('select')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </button>
          <h1 className="font-semibold">{topicTitle}</h1>
        </div>

        {/* Mode buttons */}
        <div className="flex items-center gap-2">
          {modes.map((m) => (
            <button
              key={m.value}
              onClick={() => setMode(m.value)}
              className={`mode-button flex items-center gap-2 ${
                mode === m.value ? 'active' : ''
              }`}
            >
              {m.icon}
              <span className="hidden sm:inline">{m.label}</span>
            </button>
          ))}
        </div>

        {/* Save & Map toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              showSaved
                ? 'bg-success/20 text-success'
                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {showSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            <span className="text-sm">{showSaved ? t.saved : t.save}</span>
          </button>
          <button
            onClick={toggleMapPanel}
            className={`p-2 rounded-lg transition-colors ${
              isMapPanelOpen ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
          >
            {isMapPanelOpen ? <X className="w-5 h-5" /> : <Map className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Teacher Chat Panel - LEFT */}
        <div className="w-96 flex flex-col border-r border-border bg-muted/30">
          {/* Auto-play checkbox */}
          <div className="p-2 border-b border-border flex items-center justify-end gap-2">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={autoPlayTTS}
                onChange={(e) => setAutoPlayTTS(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <Volume2 className="w-4 h-4" />
              {t.autoPlay}
            </label>
          </div>
          {/* Chat messages */}
          <div className="flex-1 overflow-auto p-4 flex flex-col">
            {messages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3"
              >
                <div className={`message ${msg.role}`}>
                  {msg.content}
                </div>
                {msg.role === 'assistant' && (
                  <div className="mt-2 px-4">
                    <TTSPlayer
                      text={msg.content}
                      language={language}
                      isFirst={index === 0}
                      autoPlay={autoPlayTTS && hasPlayedFirstTTS}
                      onPlay={() => !hasPlayedFirstTTS && setHasPlayedFirstTTS(true)}
                    />
                  </div>
                )}
              </motion.div>
            ))}
            {isLoading && (
              <div className="message assistant animate-pulse">
                Thinking...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat input */}
          <div className="p-4 border-t border-border">
            <STTInput
              value={chatInput}
              onChange={setChatInput}
              onSubmit={handleChatSubmit}
              placeholder={t.chatPlaceholder}
              disabled={isLoading}
              language={language}
            />
          </div>
        </div>

        {/* Board Panel - RIGHT */}
        <div className="flex-1 flex flex-col">
          {/* Board cells */}
          <div className="flex-1 overflow-auto p-4">
            {boardCells.map((cell) => (
              <BoardCell key={cell.id} cell={cell} />
            ))}
            <div ref={boardEndRef} />
          </div>

          {/* Board input */}
          <div className="p-4 border-t border-border">
            <STTInput
              value={boardInput}
              onChange={setBoardInput}
              onSubmit={handleBoardSubmit}
              placeholder={t.boardPlaceholder}
              disabled={isLoading}
              language={language}
            />
          </div>
        </div>

        {/* Map Panel (slide-out) */}
        <AnimatePresence>
          {isMapPanelOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-l border-border overflow-hidden"
            >
              <MapPanel showTabs />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
